import Phaser from 'phaser';
import type { Pipe } from '../entities/Pipe';
import type { MoleculeType } from '../types';

const MOL_TEXTURE: Record<MoleculeType, string> = {
  food: 'mol-food',
  oxygen: 'mol-oxygen',
  co2: 'mol-co2',
  gruel: 'mol-gruel',
  waste: 'mol-waste',
  pulse: 'mol-pulse', // unused (we never render pulse), declared for type completeness
};

// Must match FlowFx for consistency.
const MOL_SPEED_PX_PER_MS = 0.18;

/**
 * Trachea-pipe FlowFx — alternating single-sprite flow.
 *
 * Distinct from `FlowFx` (which streams continuous sprites in one direction).
 * The trachea cycle:
 *
 *   1. Ceiling sends a single O₂ sprite DOWN to lungs.
 *   2. When the O₂ arrives at lungs: if the lungs are currently emitting CO₂
 *      (set externally via `setLungsEmittingCO2(true)`), spawn one CO₂ sprite
 *      traveling UP from lungs to ceiling. Otherwise wait.
 *   3. When the CO₂ arrives at ceiling: spawn another O₂ sprite traveling
 *      DOWN.
 *   4. Cycle continues as long as the lungs keep emitting CO₂.
 *
 * If the lungs stop emitting CO₂ partway through, the cycle pauses (the
 * current in-flight sprite finishes its journey, but no new sprite is spawned).
 * Resumes immediately when `setLungsEmittingCO2(true)` is called again.
 *
 * Pipe convention: `pipe.pathPoints()[0]` is the SOURCE end (whichever side
 * is `fromPortId`). For trachea pipes we don't care which is the ceiling vs
 * lungs end for the data model; the cycle's directional state is managed
 * here. `ceilingIsAtStart` is set by Connector when constructing this fx,
 * based on the pipe's `fromPort.kind` being a wall port (vs organ port).
 */
export class TracheaFlowFx {
  private path: { x: number; y: number }[] = [];
  private legLengths: number[] = [];
  private totalLength = 0;
  private inFlight: {
    sprite: Phaser.GameObjects.Image;
    direction: 'down' | 'up';
    progressPx: number;
  } | null = null;
  private state: 'idle' | 'down-flying' | 'up-flying' | 'waiting-for-co2' = 'idle';
  private lungsEmittingCO2 = false;
  private updateHandler: () => void = () => {};
  private lastFrameTime = 0;
  private running = false;

  constructor(
    private scene: Phaser.Scene,
    private pipe: Pipe,
    /** True if the pipe's path[0] is at the ceiling end. False = path[0] is lungs. */
    private ceilingIsAtStart: boolean,
  ) {}

  /**
   * Set whether the lungs are currently producing CO₂ to expel.
   * Drives the cycle: when true and we're idle/waiting, spawn a CO₂ up.
   * When false and we're waiting, just sit; in-flight sprites continue.
   */
  setLungsEmittingCO2(emitting: boolean): void {
    this.lungsEmittingCO2 = emitting;
    if (emitting && this.state === 'waiting-for-co2') {
      this.spawnCO2Up();
    }
  }

  /** Begin the cycle. Sends the first O₂ down from the ceiling. */
  start(): void {
    if (this.running) return;
    this.path = this.pipe.pathPoints();
    if (this.path.length < 2) return;
    this.legLengths = [];
    this.totalLength = 0;
    for (let i = 0; i < this.path.length - 1; i++) {
      const dx = this.path[i + 1].x - this.path[i].x;
      const dy = this.path[i + 1].y - this.path[i].y;
      const len = Math.hypot(dx, dy);
      this.legLengths.push(len);
      this.totalLength += len;
    }
    this.lastFrameTime = this.scene.time.now;
    this.running = true;
    this.updateHandler = () => this.tick();
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.updateHandler);
    this.spawnO2Down();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.updateHandler);
    if (this.inFlight) {
      this.inFlight.sprite.destroy();
      this.inFlight = null;
    }
    this.state = 'idle';
    this.lungsEmittingCO2 = false;
  }

  private spawnO2Down(): void {
    // O₂ travels from ceiling toward lungs. If path[0] is ceiling, direction is
    // start→end; otherwise end→start.
    const dir = this.ceilingIsAtStart ? 'down' : 'up';
    this.spawnSprite('oxygen', dir);
    this.state = 'down-flying';
  }

  private spawnCO2Up(): void {
    // CO₂ travels from lungs toward ceiling.
    const dir = this.ceilingIsAtStart ? 'up' : 'down';
    this.spawnSprite('co2', dir);
    this.state = 'up-flying';
  }

  /**
   * Spawn a sprite. `direction` is in terms of path indices:
   * - 'down' = progress increases (path[0] → path[end])
   * - 'up' = progress decreases (path[end] → path[0])
   */
  private spawnSprite(mol: MoleculeType, direction: 'down' | 'up'): void {
    const startIdx = direction === 'down' ? 0 : this.path.length - 1;
    const start = this.path[startIdx];
    const sprite = this.scene.add.image(start.x, start.y, MOL_TEXTURE[mol]);
    sprite.setDepth(8);
    this.inFlight = {
      sprite,
      direction,
      progressPx: direction === 'down' ? 0 : this.totalLength,
    };
  }

  private tick(): void {
    if (!this.running) return;
    const now = this.scene.time.now;
    const dt = now - this.lastFrameTime;
    this.lastFrameTime = now;
    if (!this.inFlight) return;

    const step = dt * MOL_SPEED_PX_PER_MS;
    if (this.inFlight.direction === 'down') {
      this.inFlight.progressPx += step;
      if (this.inFlight.progressPx >= this.totalLength) {
        // Arrived at the "down" destination — either lungs (if O₂) or ceiling (if CO₂ from up-side).
        this.onSpriteArrival();
        return;
      }
    } else {
      this.inFlight.progressPx -= step;
      if (this.inFlight.progressPx <= 0) {
        this.onSpriteArrival();
        return;
      }
    }
    const pos = this.positionAlong(this.inFlight.progressPx);
    this.inFlight.sprite.setPosition(pos.x, pos.y);
  }

  private onSpriteArrival(): void {
    if (!this.inFlight) return;
    this.inFlight.sprite.destroy();
    const arrivedMol = this.state === 'down-flying' ? 'oxygen' : 'co2';
    this.inFlight = null;
    if (arrivedMol === 'oxygen') {
      // O₂ just arrived at lungs. If lungs are emitting CO₂, send one back up.
      if (this.lungsEmittingCO2) {
        this.spawnCO2Up();
      } else {
        this.state = 'waiting-for-co2';
      }
    } else {
      // CO₂ just arrived at ceiling. Always trigger the next O₂ back down.
      this.spawnO2Down();
    }
  }

  private positionAlong(distance: number): { x: number; y: number } {
    let remaining = distance;
    for (let i = 0; i < this.legLengths.length; i++) {
      const legLen = this.legLengths[i];
      if (remaining <= legLen) {
        const t = legLen === 0 ? 0 : remaining / legLen;
        return {
          x: this.path[i].x + (this.path[i + 1].x - this.path[i].x) * t,
          y: this.path[i].y + (this.path[i + 1].y - this.path[i].y) * t,
        };
      }
      remaining -= legLen;
    }
    return this.path[this.path.length - 1];
  }
}
